import { Notification } from '../services/userService';
import { shopifyMappingHref } from '../pages/integrationDetailTabs';

/**
 * Only allow relative in-app paths (and the public contact page used by
 * rejection notices). Blocks open redirects via absolute URLs or
 * protocol-relative hosts.
 */
const sanitizeAppRoute = (route: unknown): string | null => {
  if (typeof route !== 'string') return null;
  const trimmed = route.trim();
  if (!trimmed.startsWith('/app/') && trimmed !== '/contact') return null;
  if (trimmed.includes('://') || trimmed.startsWith('//')) return null;
  return trimmed;
};

const appendQuery = (
  path: string,
  params: Record<string, string | undefined | null>
): string => {
  const qIndex = path.indexOf('?');
  const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const search = new URLSearchParams(qIndex >= 0 ? path.slice(qIndex + 1) : '');
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      search.set(key, value);
    }
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
};

const brandFromData = (data: any): string | undefined => {
  if (typeof data?.brand === 'string' && data.brand.trim()) {
    return data.brand.trim().toLowerCase();
  }
  const firstBrand = data?.brands?.[0]?.brandId;
  if (typeof firstBrand === 'string' && firstBrand.trim()) {
    return firstBrand.trim().toLowerCase();
  }
  return undefined;
};

const catalogActionFromData = (data: any): string | undefined => {
  if (data?.rejectedSuspicious) return 'rejected_suspicious';
  if (data?.appliedNeedsReview) return 'applied_needs_review';
  return undefined;
};

/**
 * Attach brand / review-action filters when the destination supports them.
 */
const enrichDestination = (path: string, data: any): string => {
  const basePath = path.split('?')[0];

  if (basePath === '/app/admin/catalog-price-changes') {
    return appendQuery(path, {
      brand: brandFromData(data),
      action: catalogActionFromData(data),
    });
  }

  if (basePath === '/app/admin/pricing-freshness') {
    return appendQuery(path, {
      brand: brandFromData(data),
    });
  }

  if (basePath === '/app/shopify-pricing-audit') {
    return appendQuery(path, {
      productId: typeof data?.productId === 'string' ? data.productId : undefined,
      integrationId: typeof data?.integrationId === 'string' ? data.integrationId : undefined,
    });
  }

  if (basePath === '/app/products') {
    const sku =
      (typeof data?.sku === 'string' && data.sku) ||
      (typeof data?.changes?.[0]?.sku === 'string' && data.changes[0].sku) ||
      undefined;
    const productId = typeof data?.productId === 'string' ? data.productId : undefined;
    // Prefer the SKU for the search box when one is present: it's what the
    // product browser's search actually matches against, whereas productId
    // is an internal `brand_sku` compound key that won't hit as a search
    // term. Only fall back to productId (e.g. ordinary price-change
    // notifications) when no SKU was stored on the notification.
    const search = sku || productId || undefined;
    return appendQuery(path, {
      productId,
      search,
      brand: brandFromData(data),
    });
  }

  if (basePath === '/app/admin/canonical-products') {
    return appendQuery(path, {
      productId: typeof data?.productId === 'string' ? data.productId : undefined,
      sku:
        (typeof data?.sku === 'string' && data.sku) ||
        (typeof data?.changes?.[0]?.sku === 'string' && data.changes[0].sku) ||
        undefined,
      search:
        (typeof data?.productId === 'string' && data.productId) ||
        (typeof data?.sku === 'string' && data.sku) ||
        undefined,
    });
  }

  return path;
};

const fallbackByType = (
  type: Notification['type'],
  data: any,
  isAdmin: boolean
): string | null => {
  switch (type) {
    case 'system_update':
      if (data?.requestId || data?.vendorId) {
        return isAdmin
          ? '/app/admin/brands-vendors/approvals'
          : '/app/dashboard#application';
      }
      // Pricing coverage / ops alerts without an explicit route
      if (isAdmin && Array.isArray(data?.brands)) {
        return enrichDestination('/app/admin/pricing-freshness', data);
      }
      if (isAdmin && data?.jobId) {
        return '/app/admin/data-ingestion';
      }
      return null;
    case 'map_pricing_update':
      return '/app/map-pricing-data';
    case 'map_violation':
      if (data?.source === 'shopify') {
        return enrichDestination('/app/shopify-pricing-audit', data);
      }
      return '/app/map-pricing-data';
    case 'new_camera':
    case 'product_added':
      return enrichDestination('/app/products', data);
    case 'product_updated':
      if (isAdmin && (data?.jobId || data?.reportId || data?.discontinued)) {
        return '/app/admin/data-ingestion';
      }
      return enrichDestination('/app/products', data);
    case 'price_changed':
      if (data?.suspiciousDiscountReview && data?.productId) {
        return enrichDestination('/app/products', data);
      }
      if (data?.missingAdvertisedPriceReview && data?.productId) {
        return enrichDestination('/app/products', data);
      }
      if (
        isAdmin &&
        (data?.reportId ||
          data?.appliedNeedsReview ||
          data?.rejectedSuspicious ||
          Array.isArray(data?.changes))
      ) {
        return enrichDestination('/app/admin/catalog-price-changes', data);
      }
      return enrichDestination('/app/products', data);
    case 'integration_sync_completed':
    case 'integration_sync_failed':
      if (data?.jobId) {
        return isAdmin ? '/app/admin/data-ingestion' : '/app/integrations';
      }
      return data?.integrationId
        ? `/app/integrations/${data.integrationId}`
        : '/app/integrations';
    case 'shopify_listing_hold':
      return data?.integrationId
        ? shopifyMappingHref(data.integrationId, 'listing-matches')
        : '/app/integrations';
    case 'organization_joined':
      return '/app/team-management';
    case 'welcome':
      return '/app/dashboard';
    case 'user_role_changed':
    case 'account_suspended':
      return '/app/profile';
    default:
      return null;
  }
};

const ADMIN_ROUTE_PREFIX = '/app/admin';

const resolveDestination = (
  notification: Notification,
  isAdmin: boolean
): string | null => {
  const { type, data } = notification;

  // ERP suspicious-discount holds: open the product so admins can check
  // stored catalog pricing (MSRP / retail / sale / market).
  if (isAdmin && type === 'price_changed' && data?.suspiciousDiscountReview && data?.productId) {
    return enrichDestination('/app/products', data);
  }
  if (isAdmin && type === 'price_changed' && data?.missingAdvertisedPriceReview && data?.productId) {
    return enrichDestination('/app/products', data);
  }

  // Prefer the catalog review queue for scrape price alerts, even when an
  // older notification still stores route=/app/admin/data-ingestion.
  if (
    isAdmin &&
    type === 'price_changed' &&
    (data?.appliedNeedsReview ||
      data?.rejectedSuspicious ||
      data?.reportId ||
      Array.isArray(data?.changes))
  ) {
    return enrichDestination('/app/admin/catalog-price-changes', data);
  }

  // Coverage alerts used to land on data-ingestion; send them to the report.
  if (
    isAdmin &&
    type === 'system_update' &&
    Array.isArray(data?.brands) &&
    sanitizeAppRoute(data?.route) === '/app/admin/data-ingestion'
  ) {
    return enrichDestination('/app/admin/pricing-freshness', data);
  }

  // Listing holds used to store `?tab=mapping` (pre MOT-252) or `?tab=matching`;
  // the holds live on the Mapping tab's Catalog Matching section.
  if (type === 'shopify_listing_hold' && data?.integrationId) {
    return fallbackByType(type, data, isAdmin);
  }

  const explicit = sanitizeAppRoute(data?.route);
  if (explicit) {
    return enrichDestination(explicit, data);
  }

  return fallbackByType(type, data, isAdmin);
};

/**
 * Resolve the in-app destination for a notification, or null when there is
 * no meaningful page to open for it.
 *
 * Prefers `data.route` (same field email CTAs use), then type-based fallbacks.
 *
 * A non-admin caller is never handed an `/app/admin` route. The type fallbacks
 * already branch on `isAdmin`, but `data.route` is stored copy — plenty of
 * existing notifications carry `route: '/app/admin/data-ingestion'` — and it
 * would otherwise be returned verbatim. That matters beyond a dead link: an
 * admin viewing as someone else is navigated out of the workspace, which ends
 * the impersonation session.
 */
export const getNotificationDestination = (
  notification: Notification,
  isAdmin: boolean
): string | null => {
  const destination = resolveDestination(notification, isAdmin);
  if (isAdmin || !destination || !destination.startsWith(ADMIN_ROUTE_PREFIX)) {
    return destination;
  }

  const fallback = fallbackByType(notification.type, notification.data, false);
  return fallback && !fallback.startsWith(ADMIN_ROUTE_PREFIX) ? fallback : '/app/notifications';
};
